import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { OrganisationAllowance } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { builderCopy as copy, fill } from "./copy";
import { runDiscard, shelfView, type ShelfCard, type ShelfLoad } from "./designShelf";
import { PlanMeter } from "./PlanMeter";
import { meterView } from "./planMeterView";
import { authoringCalls, builderDeps } from "./useDraftSession";

/**
 * Being built (KEY-560), on My Dials: the dials this person is building, trying out, or running, each with its way back in. Only
 * shown to someone the builder is offered to, and only once there's something on it.
 */
export function BeingBuiltShelf() {
  const navigate = useNavigate();
  const [load, setLoad] = useState<ShelfLoad>({ kind: "loading" });
  const [allowance, setAllowance] = useState<OrganisationAllowance | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    authoringCalls()
      .listMyDesigns()
      .then(
        (designs) => {
          if (!cancelled) setLoad({ kind: "loaded", designs });
        },
        () => {
          if (!cancelled) setLoad({ kind: "failed" });
        },
      );
    builderDeps()
      .getAllowance()
      .then((answer) => {
        if (!cancelled) setAllowance(answer);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = shelfView(load);
  if (!cards) return null;

  async function discard(card: ShelfCard) {
    setConfirming(null);
    setDiscarding(card.id);
    setError(null);
    // Discarding goes to the organisation the author signed in to, whichever dial last set the active one.
    const result = await runDiscard(() => api.deleteDefinition(getAccessToken() ?? "", card.id, { homeOrg: true }));
    setDiscarding(null);

    if (result.kind === "discarded" || result.kind === "gone") {
      setLoad((previous) => (previous.kind === "loaded" ? { kind: "loaded", designs: previous.designs.filter((d) => d.id !== card.id) } : previous));
      builderDeps()
        .getAllowance()
        .then(setAllowance)
        .catch(() => {});
      return;
    }
    setError(result.kind === "refused" && result.message ? result.message : copy.waysIn.discardFailed);
  }

  const meter = meterView(allowance, "definitions");

  return (
    <section className="kd-shelf" aria-labelledby="kd-shelf-heading">
      <div className="kd-shelf-head">
        <h2 id="kd-shelf-heading" className="kd-shelf-heading">
          {copy.waysIn.shelfHeading} <span className="kd-shelf-count">· {cards.length}</span>
        </h2>
        {meter && <PlanMeter view={meter} template={copy.planMeter.designs} />}
      </div>
      <ul className="kd-shelf-list">
        {cards.map((card) => {
          const question = fill(copy.waysIn.discardConfirm, { name: card.name });
          return (
            <li key={card.id} className="kd-shelf-card">
              <div className="kd-shelf-name">{card.name}</div>
              <span className="kd-shelf-state" data-state={card.state}>
                <span className="kd-shelf-dot" aria-hidden="true" />
                {card.stateLabel}
              </span>
              <div className="kd-shelf-actions">
                <button type="button" className="kd-builder-link" onClick={() => navigate(card.action.to)}>
                  {card.action.label}
                </button>
                {card.canDiscard && (
                  <button type="button" className="kd-icon-btn" disabled={discarding === card.id} onClick={() => setConfirming(card.id)}>
                    {copy.waysIn.discard}
                  </button>
                )}
              </div>
              {confirming === card.id && (
                <div className="kd-panel-confirm" role="group" aria-label={question}>
                  <span>{question}</span>
                  <button type="button" className="kd-btn" onClick={() => setConfirming(null)}>
                    {copy.build.keepIt}
                  </button>
                  <button type="button" className="kd-btn-primary" onClick={() => void discard(card)}>
                    {copy.waysIn.discard}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {error && (
        <p className="kd-builder-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
